/* eslint-disable no-restricted-imports */
import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import {UIGameManagerMethods} from '../../../../code/client/shared/scripts/ui_game/UIGameManager'
import {UIGameSettingsMethods} from '../../../../code/client/shared/scripts/ui_game/UIGameSettingsController'
import {GameSettings} from '@colonist/game-base-types'
import {PIXI} from '@colonist/pixi'
import {StorageWrapper} from '../utils/storage'
import {Sound} from '../utils/sound'

export const UIGameSettingsContext = createContext<{
    gameManager: UIGameManagerMethods
    closeSettings: VoidFunction
    gameSettings: GameSettings
    updateCanvasSettings: (options: Partial<PIXI.IApplicationOptions>) => void
    soundVolume: number
    onChangeSound: (value: number) => void
}>({
            gameManager: {} as UIGameManagerMethods,
            closeSettings: () => null,
            gameSettings: {} as GameSettings,
            updateCanvasSettings: () => null,
            soundVolume: Sound.DEFAULT_VOLUME,
            onChangeSound: () => null,
        })

export const useUIGameSettingsContext = () =>
    useContext(UIGameSettingsContext)

interface UIGameSettingsProviderProps {
    children: ReactNode
    gameManager: UIGameManagerMethods
    gameSettings: GameSettings
}

export default function UIGameSettingsProvider({
    children,
    gameManager,
    gameSettings: defaultGameSettings,
}: UIGameSettingsProviderProps) {
    const [isOpenSettings, setIsOpenSettings] = useState(false)
    const [gameSettings, setGameSettings] = useState<GameSettings>(defaultGameSettings)
    const [soundVolume, setSoundVolume] = useState(StorageWrapper.getMasterVolume(Sound.DEFAULT_VOLUME))

    const isOpenSettingsRef = useRef(isOpenSettings)

    useEffect(() => {
        isOpenSettingsRef.current = isOpenSettings
    }, [isOpenSettings])

    const onChangeSound = (value: number) => {
        Sound.setMasterVolume(value)
        setSoundVolume(value)
    }

    const closeSettings = () => {
        setIsOpenSettings(false)
        const gameSettingsContainer = document.getElementById('ui-game-settings-container')
        if(gameSettingsContainer) {
            gameSettingsContainer.style.display = 'none'
        }
    }

    const openSettings = () => {
        setIsOpenSettings(true)
        const gameSettingsContainer = document.getElementById('ui-game-settings-container')
        if(gameSettingsContainer) {
            gameSettingsContainer.style.display = 'block'
        }
    }

    const toggleSettings = (open: boolean) => {
        if(open) {
            openSettings()
        } else {
            closeSettings()
        }
    }

    const onGameEnded = () => {
        closeSettings()
    }

    const updateGameSettings = () => {
        setGameSettings(gameManager.gameController.gameSettings)
    }

    const updateCanvasSettings = (options: Partial<PIXI.IApplicationOptions>) => {
        gameManager.canvasController.updateCanvasSettings(options)
    }

    const isActive = () => isOpenSettingsRef.current

    useLayoutEffect(() => {
        const settingsController: UIGameSettingsMethods = {
            closeSettings: closeSettings,
            toggleSettings: toggleSettings,
            onGameEnded: onGameEnded,
            updateGameSettings: updateGameSettings,
            gameHelpSettingsController: {
                audio: {
                    masterAudioController: {
                        setSlider: onChangeSound,
                    },
                },
            },
            isActive: isActive,
        }

        gameManager.settingsController = settingsController
    }, [])

    return (
        <UIGameSettingsContext.Provider
            value={{
                gameManager: gameManager,
                closeSettings: closeSettings,
                gameSettings: gameSettings,
                updateCanvasSettings: updateCanvasSettings,
                soundVolume: soundVolume,
                onChangeSound: onChangeSound,
            }}
        >
            {children}
        </UIGameSettingsContext.Provider>
    )
}
